/**
 * Die Leiste über jeder Seite, wenn die Sitzung gerade nicht geprüft werden
 * konnte, weil der Server nicht antwortet (Deploy, 502 vom Proxy, 429, 5xx).
 *
 * Der Nutzer bleibt angemeldet — bis 17.10.2026 wurde er in diesem Fall still
 * abgemeldet. Damit der Fehler trotzdem nicht unsichtbar bleibt, steht er hier,
 * mit einem Knopf, der die Prüfung wiederholt. Gelingt sie, verschwindet die
 * Leiste.
 */
import { useState } from 'react'
import { RefreshCw, WifiOff } from 'lucide-react'

import { useAuth } from '../hooks/useAuth'
import { useTranslation } from '../hooks/useTranslation'

interface Verbindung {
  verbindungsFehler: { response?: { status?: number } } | null
  sitzungPruefen: () => Promise<void>
}

export default function VerbindungsHinweis() {
  const { t } = useTranslation()
  const { verbindungsFehler, sitzungPruefen } = useAuth() as Verbindung
  const [pruefe, setPruefe] = useState(false)

  if (!verbindungsFehler) return null

  const ueberlastet = verbindungsFehler.response?.status === 429
  const erneut = async () => {
    setPruefe(true)
    try {
      await sitzungPruefen()
    } finally {
      setPruefe(false)
    }
  }

  return (
    <div
      role="alert"
      data-testid="verbindungs-hinweis"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 1001,
        background: 'var(--amber-50)',
        borderBottom: '1px solid var(--amber-200)',
        color: 'var(--amber-900)',
        padding: '10px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '8px 12px',
        fontSize: '1rem',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 10, flex: '1 1 14rem' }}>
        <WifiOff size={18} style={{ flexShrink: 0 }} aria-hidden="true" />
        {ueberlastet
          ? t('auth.verbindung.ueberlastet', 'Der Server ist gerade ausgelastet. Du bleibst angemeldet.')
          : t('auth.verbindung.nicht_erreichbar', 'Der Server ist gerade nicht erreichbar. Du bleibst angemeldet.')}
      </span>
      <button
        type="button"
        onClick={erneut}
        disabled={pruefe}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          background: 'var(--amber-800)',
          color: 'var(--weiss)',
          border: 'none',
          borderRadius: 'var(--radius-sm)',
          padding: '6px 12px',
          fontWeight: 600,
          fontSize: '1rem',
          cursor: pruefe ? 'wait' : 'pointer',
        }}
      >
        <RefreshCw size={14} aria-hidden="true" style={pruefe ? { animation: 'spin 1s linear infinite' } : undefined} />
        {pruefe
          ? t('auth.verbindung.pruefe', 'Verbindung wird geprüft …')
          : t('auth.verbindung.erneut', 'Erneut versuchen')}
      </button>
    </div>
  )
}
